import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Row, 
    Col, 
    Avatar, 
    Typography, 
    Statistic, 
    Button, 
    Space, 
    Progress,
    List,
    Tag,
    Divider,
    Spin,
    message
} from 'antd';
import { 
    UserOutlined, 
    BookOutlined, 
    ClockCircleOutlined, 
    TrophyOutlined,
    SettingOutlined,
    HistoryOutlined,
    EditOutlined,
    PlusOutlined,
    FireOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// 扩展dayjs以支持fromNow方法
dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;

const UserDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const baseURL = 'http://localhost:8080/api';

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${baseURL}/user/dashboard`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setDashboardData(response.data.data);
            } else {
                message.error(response.data.message || 'Failed to load dashboard data');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            if (error.response?.status === 401) {
                message.error('Please login again');
                navigate('/login');
            } else {
                // 如果API不可用，使用模拟数据
                console.log('API not available, using mock data');
                setDashboardData({
                    user_info: {
                        username: 'User',
                        email: 'user@example.com',
                        avatar: null,
                        preferences: {
                            language: 'English',
                            depth: 'Medium'
                        }
                    },
                    statistics: {
                        completed_courses: 12,
                        total_study_time: 86400, // 24小时
                        achievements: 8,
                        study_streak: 5
                    },
                    recent_activities: [
                        {
                            id: '1',
                            type: 'course_completed',
                            title: 'Completed React Basics',
                            description: 'Congratulations on completing the React Basics course!',
                            timestamp: new Date().toISOString(),
                            course_id: 'react-basics'
                        },
                        {
                            id: '2',
                            type: 'course_started',
                            title: 'Started Advanced JavaScript',
                            description: 'You have started learning Advanced JavaScript concepts',
                            timestamp: new Date(Date.now() - 86400000).toISOString(),
                            course_id: 'advanced-js'
                        }
                    ]
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const formatStudyTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'course_completed':
                return <TrophyOutlined style={{ color: '#52c41a' }} />;
            case 'course_started':
                return <BookOutlined style={{ color: '#1890ff' }} />;
            case 'achievement_earned':
                return <TrophyOutlined style={{ color: '#faad14' }} />;
            default:
                return <BookOutlined />;
        }
    };

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '400px' 
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!dashboardData) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Text type="secondary">Failed to load dashboard data</Text>
            </div>
        );
    }

    const { user_info, statistics, recent_activities } = dashboardData;

    return (
        <div style={{ 
            padding: '24px',
            background: '#f5f5f5',
            minHeight: 'calc(100vh - 64px)'
        }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                {/* 用户信息卡片 */}
                <Card style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Row gutter={24} align="middle">
                        <Col>
                            <Avatar 
                                size={80} 
                                src={user_info.avatar}
                                icon={<UserOutlined />}
                                style={{ backgroundColor: '#1890ff' }}
                            />
                        </Col>
                        <Col flex={1}>
                            <Title level={3} style={{ margin: 0 }}>
                                {user_info.username}
                            </Title>
                            <Text type="secondary" style={{ fontSize: 16 }}>
                                {user_info.email}
                            </Text>
                            <div style={{ marginTop: 8 }}>
                                <Tag color="blue">
                                    Preferred Language: {user_info.preferences?.language || 'English'}
                                </Tag>
                                <Tag color="green">
                                    Learning Depth: {user_info.preferences?.depth || 'Medium'}
                                </Tag>
                            </div>
                        </Col>
                        <Col>
                            <Space direction="vertical">
                                <Button 
                                    type="primary" 
                                    icon={<EditOutlined />}
                                    onClick={() => navigate('/settings')}
                                >
                                    Edit Profile
                                </Button>
                                <Button 
                                    icon={<HistoryOutlined />}
                                    onClick={() => navigate('/history')}
                                >
                                    View History
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {/* 统计信息 */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
                            <Statistic
                                title="Courses Completed"
                                value={statistics.completed_courses || 0}
                                prefix={<BookOutlined style={{ color: '#1890ff' }} />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
                            <Statistic
                                title="Study Hours"
                                value={formatStudyTime(statistics.total_study_time || 0)}
                                prefix={<ClockCircleOutlined style={{ color: '#52c41a' }} />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
                            <Statistic
                                title="Achievements"
                                value={statistics.achievements || 0}
                                prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card style={{ textAlign: 'center', borderRadius: 12 }}>
                            <Statistic
                                title="Learning Streak"
                                value={statistics.study_streak || 0}
                                prefix={<TrophyOutlined style={{ color: '#f5222d' }} />}
                                valueStyle={{ color: '#f5222d' }}
                                suffix="days"
                            />
                        </Card>
                    </Col>
                </Row>

                <Row gutter={[24, 24]}>
                    {/* 最近学习的课程 */}
                    <Col xs={24} lg={16}>
                        <Card 
                            title="Recent Learning Activity" 
                            style={{ borderRadius: 12 }}
                            extra={
                                <Button 
                                    type="link" 
                                    onClick={() => navigate('/course-selection')}
                                    icon={<PlusOutlined />}
                                >
                                    Start New Course
                                </Button>
                            }
                        >
                            {recent_activities.length > 0 ? (
                                <List
                                    dataSource={recent_activities}
                                    renderItem={(activity) => (
                                        <List.Item
                                            actions={[
                                                <Button 
                                                    type="link" 
                                                    onClick={() => navigate(`/course/${activity.course_id}`)}
                                                >
                                                    Continue
                                                </Button>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <Avatar 
                                                        icon={getActivityIcon(activity.type)} 
                                                        style={{ backgroundColor: activity.type === 'course_completed' ? '#52c41a' : activity.type === 'course_started' ? '#1890ff' : '#faad14' }}
                                                    />
                                                }
                                                title={activity.title}
                                                description={
                                                    <div>
                                                        <Text type="secondary">
                                                            {activity.description}
                                                        </Text>
                                                        <br />
                                                        <Text type="secondary">
                                                            {dayjs(activity.timestamp).fromNow()}
                                                        </Text>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <BookOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                                    <Paragraph type="secondary">
                                        No recent learning activity. Start your first course!
                                    </Paragraph>
                                    <Button 
                                        type="primary" 
                                        onClick={() => navigate('/course-selection')}
                                        icon={<PlusOutlined />}
                                    >
                                        Browse Courses
                                    </Button>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* 快速操作 */}
                    <Col xs={24} lg={8}>
                        <Card title="Quick Actions" style={{ borderRadius: 12 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button 
                                    block 
                                    size="large"
                                    icon={<BookOutlined />}
                                    onClick={() => navigate('/course-selection')}
                                >
                                    Browse All Courses
                                </Button>
                                <Button 
                                    block 
                                    size="large"
                                    icon={<HistoryOutlined />}
                                    onClick={() => navigate('/history')}
                                >
                                    Learning History
                                </Button>
                                <Button 
                                    block 
                                    size="large"
                                    icon={<SettingOutlined />}
                                    onClick={() => navigate('/settings')}
                                >
                                    Account Settings
                                </Button>
                            </Space>

                            <Divider />

                            <div>
                                <Title level={5}>Learning Preferences</Title>
                                <div style={{ marginBottom: 8 }}>
                                    <Text strong>Language: </Text>
                                    <Tag color="blue">{user_info.preferences?.language || 'English'}</Tag>
                                </div>
                                <div style={{ marginBottom: 8 }}>
                                    <Text strong>Depth: </Text>
                                    <Tag color="green">{user_info.preferences?.depth || 'Medium'}</Tag>
                                </div>
                                <Button 
                                    type="link" 
                                    size="small"
                                    onClick={() => navigate('/settings')}
                                >
                                    Update Preferences
                                </Button>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default UserDashboard; 